import { toast } from "sonner";
import { BaseToast } from "@/components/common/BaseToast";

/**
 * Options for showing a toast notification
 */
export interface ShowToastOptions {
  title: string;
  message?: string;
  txHash?: string;
  details?: Array<{ label: string; value: string }>;
  duration?: number;
}

/**
 * Unified toast notification system
 *
 * Usage:
 *   showToast.success({ title: "Success!", message: "Operation completed" })
 *   showToast.error({ title: "Error", message: "Something went wrong", txHash: "0x..." })
 *   showToast.pending("Processing", "Please wait...")
 *
 * Features:
 *   - Consistent design across all toasts
 *   - Automatic txHash copy button and explorer link
 *   - Optional details display
 */
export const showToast = {
  /**
   * Show a success toast (green checkmark)
   */
  success: (options: ShowToastOptions) => {
    toast.custom(
      () => (
        <BaseToast
          status="success"
          title={options.title}
          message={options.message}
          txHash={options.txHash}
          details={options.details}
        />
      ),
      { duration: options.duration },
    );
  },

  /**
   * Show an error toast (red X)
   */
  error: (options: ShowToastOptions) => {
    toast.custom(
      () => (
        <BaseToast
          status="error"
          title={options.title}
          message={options.message}
          txHash={options.txHash}
          details={options.details}
        />
      ),
      { duration: options.duration },
    );
  },

  /**
   * Show a pending/loading toast (spinner)
   */
  pending: (options: ShowToastOptions | string, message?: string) => {
    // Support both old API (title, message) and new API (options object)
    if (typeof options === "string") {
      toast.custom(() => (
        <BaseToast status="pending" title={options} message={message} />
      ));
    } else {
      toast.custom(
        () => (
          <BaseToast
            status="pending"
            title={options.title}
            message={options.message}
            txHash={options.txHash}
            details={options.details}
          />
        ),
        { duration: options.duration },
      );
    }
  },

  /**
   * Show an info toast (info icon)
   */
  info: (options: ShowToastOptions) => {
    toast.custom(
      () => (
        <BaseToast
          status="info"
          title={options.title}
          message={options.message}
          txHash={options.txHash}
          details={options.details}
        />
      ),
      { duration: options.duration },
    );
  },
};
